import graphqlClient from './graphqlClient';
import {
  ActionItem, CustomerHealthDetail, Deal, DealStatus, HealthProduct, HealthStatus,
  HistoryEntry, InstalledProduct, PortfolioRow, PortfolioSummary,
} from '../pages/customerHealth/types';
import { isActionBreached } from '../pages/customerHealth/helpers';

/**
 * Customer Health service layer. Three scoped read paths — a paginated portfolio page, a
 * whole-portfolio KPI summary, and a single-parent detail — all backed by the tracked
 * `app.customer_health_portfolio` view + native `customer_health` tables, plus the edit mutations.
 * Raw shapes mirror the DB exactly (see server/hasura/migrations/.../create_customer_health and
 * .../create_customer_health_portfolio_view). ae/csam/sponsor/owner/bdo/changed_by stay as bare
 * user ids — the UI resolves them via useUsersLookup/userDisplayName.
 */

/* ============================ product catalog ============================ */
/** Canonical product_key -> display name, and (for install-derived products) the `udm.poi` column
 * that signals it's installed. `cart`/`face` are legacy `customer_health_product.product_key`
 * values — `normalizeProductKey` aliases them to the current canonical keys. */
export const PRODUCT_CATALOG: { key: string; column: string; name: string }[] = [
  { key: 'purchek', column: 'purchek_install_date', name: 'Purchek' },
  { key: 'cart_containment', column: 'containment_install_date', name: 'Cart Containment' },
  { key: 'facefirst', column: 'camera_install_date', name: 'FaceFirst' },
  { key: 'cart_manager', column: 'cart_manager_install_date', name: 'Cart Manager' },
  { key: 'wheel', column: 'paired_wheel_install_date', name: 'SmartWheel' },
];
const PRODUCT_KEY_ALIASES: Record<string, string> = { cart: 'cart_containment', face: 'facefirst' };
export const normalizeProductKey = (key: string) => PRODUCT_KEY_ALIASES[key] || key;
const PRODUCT_NAME_BY_KEY: Record<string, string> = Object.fromEntries(PRODUCT_CATALOG.map((p) => [p.key, p.name]));
const titleCase = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/* ============================ raw response shapes ============================ */
interface RawInstalledProduct { key: string; name: string; installedOn: string; }

interface RawPortfolioRow {
  ultimate_parent: string;
  track_status: string;
  sentiment_status: string;
  budget_status: string;
  channel: string | null;
  sites_total: number;
  sites_acquired: number;
  backlog_amount: number | string | null;
  annual_budget: number | string | null;
  revenue_ytd: number | string | null;
  open_actions: number;
  breached_actions: number;
  open_pipeline_value: number | string | null;
  open_pipeline_count: number;
  account_executive_id: string | null;
  cs_account_manager_id: string | null;
  installed_products: RawInstalledProduct[] | null;
}

interface RawProduct { id: string; product_key: string; health_status: string; threshold: string | null; metrics: { val: string; label: string }[] | null; notes: string | null; }
interface RawComment { id: string; comment: string; created_by: string | null; created_at: string; }
interface RawActionItem { id: string; title: string; status: string; opened_on: string; tat_days: number; owner_id: string | null; comments: RawComment[]; }
interface RawDeal { id: string; brand_name: string; bdo_id: string | null; products: string[] | null; amount: number | string; tat_days: number | null; confidence: number | null; status: string; }
interface RawChangeLog { id: string; field: string; from_value: string | null; to_value: string | null; note: string | null; created_at: string; changed_by: string | null; entry_type: string; action_item_id: string | null; }
interface RawDetail {
  ultimate_parent: string;
  track_status: string; sentiment_status: string; budget_status: string; performance: string;
  assessment_summary: string | null;
  backlog_amount: number | string | null; annual_budget: number | string | null; forecast_amount: number | string | null;
  account_executive_id: string | null; cs_account_manager_id: string | null; executive_sponsor_id: string | null;
  updated_at: string; updated_by_user: { name: string } | null;
  products: RawProduct[]; action_items: RawActionItem[]; deals: RawDeal[]; change_logs: RawChangeLog[];
}
interface RawEnrichment { channel: string | null; sites_total: number; sites_acquired: number; revenue_ytd: number | string | null; installed_products: RawInstalledProduct[] | null; }

const num = (v: number | string | null | undefined) => Number(v) || 0;

/* ============================ mappers ============================ */
const mapInstalled = (list: RawInstalledProduct[] | null): InstalledProduct[] =>
  (list || []).map((p) => ({ key: p.key, name: p.name, installedOn: p.installedOn }));

const mapPortfolioRow = (r: RawPortfolioRow): PortfolioRow => ({
  ultimateParent: r.ultimate_parent,
  trackStatus: r.track_status as HealthStatus,
  sentimentStatus: r.sentiment_status as HealthStatus,
  budgetStatus: r.budget_status as HealthStatus,
  channel: r.channel || '—',
  sitesAcquired: r.sites_acquired || 0,
  sitesTotal: r.sites_total || 0,
  backlogAmount: num(r.backlog_amount),
  annualBudget: num(r.annual_budget),
  revenueYtd: num(r.revenue_ytd),
  openActions: r.open_actions || 0,
  breachedActions: r.breached_actions || 0,
  openPipelineValue: num(r.open_pipeline_value),
  openPipelineCount: r.open_pipeline_count || 0,
  accountExecutiveId: r.account_executive_id,
  csAccountManagerId: r.cs_account_manager_id,
  installedProducts: mapInstalled(r.installed_products),
});

/** Maps a `customer_health_change_log` row to the Change History timeline entry. */
export const changeLogToHistoryEntry = (c: RawChangeLog, tone: HealthStatus | null = null): HistoryEntry => ({
  id: c.id,
  field: c.field,
  from: c.from_value || '—',
  to: c.to_value || '—',
  note: c.note || '',
  userId: c.changed_by || 'Unknown',
  at: c.created_at,
  tone,
  entryType: c.entry_type === 'comment' ? 'comment' : 'edit',
});

const mapProduct = (p: RawProduct): HealthProduct => {
  const key = normalizeProductKey(p.product_key);
  return {
    key,
    name: PRODUCT_NAME_BY_KEY[key] || titleCase(key),
    status: p.health_status as HealthStatus,
    threshold: p.threshold || '—',
    metrics: Array.isArray(p.metrics) ? p.metrics : [],
  };
};

const mapActionItem = (a: RawActionItem): ActionItem => ({
  id: a.id,
  title: a.title,
  ownerId: a.owner_id || 'Unassigned',
  status: a.status as ActionItem['status'],
  openedOn: a.opened_on,
  tatDays: a.tat_days,
  breached: isActionBreached(a.opened_on, a.tat_days, a.status as ActionItem['status']),
  comments: a.comments.map((c) => ({ id: c.id, text: c.comment, userId: c.created_by || 'Unknown', createdAt: c.created_at })),
});

const mapDeal = (d: RawDeal): Deal => ({
  id: d.id,
  brandName: d.brand_name,
  pitcherId: d.bdo_id || 'Unassigned',
  products: d.products || [],
  amount: num(d.amount),
  tatDays: d.tat_days || 30,
  confidence: d.confidence || 0,
  status: d.status as DealStatus,
});

const mapDetail = (d: RawDetail, e: RawEnrichment | null): CustomerHealthDetail => ({
  ultimateParent: d.ultimate_parent,
  trackStatus: d.track_status as HealthStatus,
  sentimentStatus: d.sentiment_status as HealthStatus,
  budgetStatus: d.budget_status as HealthStatus,
  performance: d.performance as HealthStatus,
  assessmentSummary: d.assessment_summary || '',
  channel: e?.channel || '—',
  sitesAcquired: e?.sites_acquired || 0,
  sitesTotal: e?.sites_total || 0,
  revenueYtd: num(e?.revenue_ytd),
  financials: { backlog: num(d.backlog_amount), annualBudget: num(d.annual_budget), forecast: num(d.forecast_amount) },
  team: {
    accountExecutiveId: d.account_executive_id || '—',
    csAccountManagerId: d.cs_account_manager_id || '—',
    executiveSponsorId: d.executive_sponsor_id || '—',
  },
  products: d.products.map(mapProduct),
  installedProducts: mapInstalled(e?.installed_products || null),
  actionItems: d.action_items.map(mapActionItem),
  deals: d.deals.map(mapDeal),
  history: d.change_logs.map((c) => changeLogToHistoryEntry(c)),
  updatedAt: d.updated_at || null,
  updatedByName: d.updated_by_user?.name || null,
});

/* ============================ portfolio page (paginated) ============================ */
const ORDER_BY: Record<string, unknown> = {
  'Name (A-Z)': [{ ultimate_parent: 'asc' }],
  'Name (Z-A)': [{ ultimate_parent: 'desc' }],
};

/** One page of the tracked portfolio (from the view), sorted by name, plus the total row count. */
export const fetchCustomerHealthPortfolioPage = async (args: {
  limit: number;
  offset: number;
  sortBy: string;
}): Promise<{ rows: PortfolioRow[]; totalCount: number }> => {
  const query = `
    query CustomerHealthPortfolioPage($limit: Int!, $offset: Int!, $orderBy: [app_customer_health_portfolio_order_by!]!) {
      app_customer_health_portfolio(limit: $limit, offset: $offset, order_by: $orderBy) {
        ultimate_parent
        track_status sentiment_status budget_status
        channel sites_total sites_acquired
        backlog_amount annual_budget revenue_ytd
        open_actions breached_actions open_pipeline_value open_pipeline_count
        account_executive_id cs_account_manager_id
        installed_products
      }
      app_customer_health_portfolio_aggregate {
        aggregate { count }
      }
    }
  `;
  const resp = await graphqlClient.request<{
    app_customer_health_portfolio: RawPortfolioRow[];
    app_customer_health_portfolio_aggregate: { aggregate: { count: number } };
  }>(query, { limit: args.limit, offset: args.offset, orderBy: ORDER_BY[args.sortBy] || ORDER_BY['Name (A-Z)'] });

  return {
    rows: (resp.app_customer_health_portfolio || []).map(mapPortfolioRow),
    totalCount: resp.app_customer_health_portfolio_aggregate?.aggregate?.count || 0,
  };
};

/* ============================ portfolio summary (KPI band) ============================ */
/** Whole-portfolio aggregates for the KPI band — independent of pagination. */
export const fetchCustomerHealthSummary = async (): Promise<PortfolioSummary> => {
  const query = `
    query CustomerHealthSummary {
      total: app_customer_health_portfolio_aggregate {
        aggregate {
          count
          sum { backlog_amount annual_budget revenue_ytd open_pipeline_value open_pipeline_count open_actions breached_actions }
        }
      }
      healthy: app_customer_health_portfolio_aggregate(where: { track_status: { _eq: "healthy" } }) { aggregate { count } }
      watch: app_customer_health_portfolio_aggregate(where: { track_status: { _eq: "watch" } }) { aggregate { count } }
      critical: app_customer_health_portfolio_aggregate(where: { track_status: { _eq: "critical" } }) { aggregate { count } }
    }
  `;
  const resp = await graphqlClient.request<{
    total: { aggregate: { count: number; sum: Record<string, number | string | null> } };
    healthy: { aggregate: { count: number } };
    watch: { aggregate: { count: number } };
    critical: { aggregate: { count: number } };
  }>(query);

  const sum = resp.total?.aggregate?.sum || {};
  return {
    trackedCount: resp.total?.aggregate?.count || 0,
    healthyCount: resp.healthy?.aggregate?.count || 0,
    watchCount: resp.watch?.aggregate?.count || 0,
    criticalCount: resp.critical?.aggregate?.count || 0,
    totalBacklog: num(sum.backlog_amount),
    totalRevenueYtd: num(sum.revenue_ytd),
    totalAnnualBudget: num(sum.annual_budget),
    openPipelineValue: num(sum.open_pipeline_value),
    openPipelineCount: num(sum.open_pipeline_count),
    openActions: num(sum.open_actions),
    breachedActions: num(sum.breached_actions),
  };
};

/* ============================ detail (single parent) ============================ */
/** A single parent's full record: core + nested children from `customer_health_by_pk`, plus the
 * view's udm-derived enrichment (channel/sites/revenue/installed) — one round trip, two root fields.
 * Returns null when the parent isn't found (or isn't visible to the current role). */
export const fetchCustomerHealthDetail = async (ultimateParent: string): Promise<CustomerHealthDetail | null> => {
  const query = `
    query CustomerHealthDetail($up: String!) {
      detail: app_customer_health_by_pk(ultimate_parent: $up) {
        ultimate_parent
        track_status sentiment_status budget_status performance assessment_summary
        backlog_amount annual_budget forecast_amount
        account_executive_id cs_account_manager_id executive_sponsor_id
        updated_at
        updated_by_user { name }
        products { id product_key health_status threshold metrics notes }
        action_items(order_by: { opened_on: desc }) {
          id title status opened_on tat_days owner_id
          comments(order_by: { created_at: asc }) { id comment created_by created_at }
        }
        deals(order_by: { created_at: desc }) { id brand_name bdo_id products amount tat_days confidence status }
        change_logs(order_by: { created_at: desc }) { id field from_value to_value note created_at changed_by entry_type action_item_id }
      }
      enrichment: app_customer_health_portfolio(where: { ultimate_parent: { _eq: $up } }, limit: 1) {
        channel sites_total sites_acquired revenue_ytd installed_products
      }
    }
  `;
  const resp = await graphqlClient.request<{ detail: RawDetail | null; enrichment: RawEnrichment[] }>(query, { up: ultimateParent });
  if (!resp.detail) return null;
  return mapDetail(resp.detail, resp.enrichment?.[0] || null);
};

/* ============================ trackable-parent picker (Add Ultimate Parent) ============================ */
export interface TrackableParent {
  ultimate_parent: string;
  stores: number;
  brands: number;
  total_carts: number;
}

const DEFAULT_LIMIT = 10;

/**
 * Ultimate parents with at least one store, matching an optional search term, excluding names
 * already tracked. Backed by the `getTrackableParents` action: a single SQL rollup that groups
 * udm.brand by ultimate_parent, computes store/brand counts + total cart footprint, and orders by
 * total_carts DESC (biggest customers first) — the sort + limit happen in the query, not on the
 * response, which a Hasura `distinct_on` picker can't do (its order_by is locked to the parent name).
 */
export const fetchTrackableParents = async (
  search: string = '',
  excludeParents: string[] = [],
  limit: number = DEFAULT_LIMIT
): Promise<TrackableParent[]> => {
  const query = `
    query GetTrackableParents($input: GetTrackableParentsInput!) {
      getTrackableParents(input: $input) {
        success
        data {
          ultimate_parent
          stores
          brands
          total_carts
        }
      }
    }
  `;

  try {
    const resp = await graphqlClient.request<{
      getTrackableParents: { success: boolean; data: TrackableParent[] } | null;
    }>(query, { input: { search: search.trim(), excludeParents, limit } });

    return resp.getTrackableParents?.data || [];
  } catch {
    return [];
  }
};

/**
 * Start tracking an ultimate parent: inserts its customer_health row and assigns the current user
 * so select/update permissions resolve immediately. Both inserts are on_conflict-do-nothing.
 * insert_permissions currently only allow administrator/executive.
 */
export const addCustomerHealthParent = async (ultimateParent: string, userId: string): Promise<void> => {
  const mutation = `
    mutation AddCustomerHealthParent($ultimateParent: String!, $userId: uuid!) {
      insert_app_customer_health_one(
        object: { ultimate_parent: $ultimateParent }
        on_conflict: { constraint: customer_health_pkey, update_columns: [] }
      ) {
        ultimate_parent
      }
      insert_app_user_assignment_one(
        object: { user_id: $userId, ultimate_parent: $ultimateParent }
        on_conflict: { constraint: user_assignment_user_id_ultimate_parent_key, update_columns: [] }
      ) {
        id
      }
    }
  `;
  await graphqlClient.request(mutation, { ultimateParent, userId });
};

/* ============================ health record mutations (edit path) ============================ */
export interface CustomerHealthPatch {
  track_status?: string;
  sentiment_status?: string;
  budget_status?: string;
  performance?: string;
  assessment_summary?: string;
  backlog_amount?: number;
  annual_budget?: number;
  forecast_amount?: number;
  account_executive_id?: string | null;
  cs_account_manager_id?: string | null;
  executive_sponsor_id?: string | null;
}

/** Patches the root `customer_health` row (RYG statuses, assessment, financials, team) and stamps
 * `updated_by`/`updated_at` — backs the `temp`/`text`/`money`/`hero` edit kinds. */
export const updateCustomerHealth = async (ultimateParent: string, patch: CustomerHealthPatch, updatedBy: string): Promise<void> => {
  const mutation = `
    mutation UpdateCustomerHealth($ultimateParent: String!, $set: app_customer_health_set_input!) {
      update_app_customer_health_by_pk(pk_columns: { ultimate_parent: $ultimateParent }, _set: $set) { updated_at }
    }
  `;
  await graphqlClient.request(mutation, { ultimateParent, set: { ...patch, updated_by: updatedBy } });
};

/** Inserts a new action item — backs the `newAction` edit kind. */
export const insertActionItem = async (
  ultimateParent: string,
  input: { title: string; owner_id: string | null; status: string; tat_days: number }
): Promise<void> => {
  const mutation = `
    mutation InsertActionItem($object: app_customer_health_action_item_insert_input!) {
      insert_app_customer_health_action_item_one(object: $object) { id }
    }
  `;
  await graphqlClient.request(mutation, { object: { ultimate_parent: ultimateParent, ...input } });
};

/** Updates an existing action item's status — backs the `status` edit kind. */
export const updateActionItemStatus = async (id: string, status: string): Promise<void> => {
  const mutation = `
    mutation UpdateActionItemStatus($id: uuid!, $status: String!) {
      update_app_customer_health_action_item_by_pk(pk_columns: { id: $id }, _set: { status: $status }) { id }
    }
  `;
  await graphqlClient.request(mutation, { id, status });
};

/** Adds a comment to an action item's thread — direct add, not a logged/audited edit. */
export const insertActionItemComment = async (actionItemId: string, comment: string, createdBy: string): Promise<void> => {
  const mutation = `
    mutation InsertActionItemComment($object: app_customer_health_action_item_comment_insert_input!) {
      insert_app_customer_health_action_item_comment_one(object: $object) { id }
    }
  `;
  await graphqlClient.request(mutation, { object: { action_item_id: actionItemId, comment, created_by: createdBy } });
};

/** Upserts a product's health status/metrics by (ultimate_parent, product_key) — backs `product`. */
export const upsertProduct = async (
  ultimateParent: string,
  productKey: string,
  patch: { health_status?: string; metrics?: { val: string; label: string }[] }
): Promise<void> => {
  const mutation = `
    mutation UpsertProduct($object: app_customer_health_product_insert_input!) {
      insert_app_customer_health_product_one(
        object: $object
        on_conflict: { constraint: customer_health_product_ultimate_parent_product_key_key, update_columns: [health_status, metrics] }
      ) { id }
    }
  `;
  await graphqlClient.request(mutation, { object: { ultimate_parent: ultimateParent, product_key: productKey, ...patch } });
};

/** Inserts a new pipeline deal — backs adding a deal from the `deal` edit kind. */
export const insertDeal = async (
  ultimateParent: string,
  input: { brand_name: string; bdo_id: string | null; products: string[]; amount: number; tat_days: number; confidence: number; status: string }
): Promise<void> => {
  const mutation = `
    mutation InsertDeal($object: app_customer_health_deal_insert_input!) {
      insert_app_customer_health_deal_one(object: $object) { id }
    }
  `;
  await graphqlClient.request(mutation, { object: { ultimate_parent: ultimateParent, ...input } });
};

/** Updates an existing pipeline deal — backs editing a deal from the `deal` edit kind. */
export const updateDeal = async (
  id: string,
  patch: { brand_name?: string; bdo_id?: string | null; products?: string[]; amount?: number; tat_days?: number; confidence?: number; status?: string }
): Promise<void> => {
  const mutation = `
    mutation UpdateDeal($id: uuid!, $set: app_customer_health_deal_set_input!) {
      update_app_customer_health_deal_by_pk(pk_columns: { id: $id }, _set: $set) { id }
    }
  `;
  await graphqlClient.request(mutation, { id, set: patch });
};

/** Appends a change_log row for the Change History timeline. Defaults to `entry_type: 'edit'`;
 * pass `entry_type: 'comment'` + `action_item_id` for an action-item comment marker. */
export const insertChangeLog = async (
  ultimateParent: string,
  input: {
    field: string;
    from_value: string | null;
    to_value: string | null;
    note: string;
    changed_by: string;
    entry_type?: 'edit' | 'comment';
    action_item_id?: string | null;
  }
): Promise<void> => {
  const mutation = `
    mutation InsertChangeLog($object: app_customer_health_change_log_insert_input!) {
      insert_app_customer_health_change_log_one(object: $object) { id }
    }
  `;
  await graphqlClient.request(mutation, { object: { ultimate_parent: ultimateParent, entry_type: 'edit', ...input } });
};
